import { useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { fromQcTemplate, toQcTemplateBody } from '../api/mappers';
import { QcChecklistForm } from '../components/qc-checklist-form';
import { useQcTemplate } from '../hooks/use-qc-template';
import { useCreateQcTemplate, useUpdateQcTemplate } from '../hooks/use-qc-template-mutations';
import { emptyQcField } from '../schemas/qc-form-schema';

function QcChecklistCreate() {
  const { t } = useTranslation('workflows');
  const store = useStore();
  const navigate = useNavigate();
  const create = useCreateQcTemplate(store.id);

  return (
    <PageContent>
      <PageHeader
        title={t('qc.createTitle')}
        backTo={{ to: '/s/$storeId/settings/qc-checklists', params: { storeId: store.id } }}
        backLabel={t('qc.title')}
      />
      <QcChecklistForm
        defaultValues={{ name: '', fields: [emptyQcField()] }}
        isSubmitting={create.isPending}
        submitLabel={t('qc.create')}
        onSubmit={async (values) => {
          const created = await create.mutateAsync(toQcTemplateBody(values));
          await navigate({
            to: '/s/$storeId/settings/qc-checklists/$checklistId',
            params: { storeId: store.id, checklistId: created.id },
            replace: true,
          });
        }}
      />
    </PageContent>
  );
}

function QcChecklistEdit() {
  const { t } = useTranslation('workflows');
  const format = useFormat();
  const store = useStore();
  const { editable, reason } = useStoreEditable({ permission: 'settings.write' });
  // Not strict so the page also renders under a test router; the route always has the param.
  const { checklistId = '' } = useParams({ strict: false });
  // Prefetched by the route loader, so this resolves from cache.
  const { data: checklist } = useQcTemplate(store.id, checklistId);
  const update = useUpdateQcTemplate(store.id);
  if (!checklist) return null;

  return (
    <PageContent>
      <PageHeader
        title={checklist.name}
        description={
          <span title={format.utc(checklist.updatedAt)}>
            {t('qc.fieldCount', { count: checklist.fields.length })}
            {' · '}
            {t('updatedAt', { when: format.relative(checklist.updatedAt) })}
            {' · '}
            {t('qc.runningNote')}
          </span>
        }
        backTo={{ to: '/s/$storeId/settings/qc-checklists', params: { storeId: store.id } }}
        backLabel={t('qc.title')}
      />
      <ReadOnlyBanner reason={reason} />
      <QcChecklistForm
        key={checklist.updatedAt}
        defaultValues={fromQcTemplate(checklist)}
        readOnly={!editable}
        isSubmitting={update.isPending}
        submitLabel={t('qc.save')}
        onSubmit={(values) =>
          update.mutateAsync({ id: checklist.id, body: toQcTemplateBody(values) })
        }
      />
    </PageContent>
  );
}

export function QcChecklistCreatePage() {
  return (
    <FeatureErrorBoundary name="workflows.qc.create">
      <QcChecklistCreate />
    </FeatureErrorBoundary>
  );
}

export function QcChecklistEditPage() {
  return (
    <FeatureErrorBoundary name="workflows.qc.edit">
      <QcChecklistEdit />
    </FeatureErrorBoundary>
  );
}
